/**
 * Raccourcis clavier du coffre et focus conserve au re-rendu. Module pur, zero E/S : il traduit une touche en
 * COMMANDE ; c'est `app.ts` qui l'execute, par les memes fonctions que les clics (meme garde de re-demande du mot
 * de passe maitre, meme lecture du secret au moment du geste, jamais dans le DOM).
 *
 * Deux familles, comme chez les leaders :
 *   - lettres seules, hors de tout champ de saisie (a la Gmail / GitHub / Proton Pass) : `/` rechercher, `j` / `k`
 *     entree suivante / precedente, `n` nouvelle entree, `e` modifier, `u` / `p` / `t` copier identifiant / mot de
 *     passe / code 2FA, `g` grand format du mot de passe, `l` verrouiller, `?` la liste des raccourcis ;
 *   - avec Ctrl (Cmd sur Mac), a la KeePassXC / 1Password : Ctrl+C copie le mot de passe de l'entree ouverte
 *     QUAND RIEN N'EST SELECTIONNE (sinon la copie du texte selectionne reste celle du navigateur), Ctrl+B
 *     l'identifiant ; ils marchent aussi depuis la recherche, ce qui fait « chercher, fleche, copier » sans souris.
 * Dans la recherche : fleches bas / haut parcourent la liste sans quitter le champ, Echap l'efface.
 *
 * Jamais dans un autre champ de saisie (editeur, mot de passe maitre) : une lettre y est une lettre. Jamais pendant
 * une composition (accents, IME). Aucun raccourci n'ecrase un raccourci reserve du navigateur (Ctrl+T, Ctrl+L,
 * Ctrl+Maj+C restent a lui).
 *
 * Reglables (WCAG 2.1 critere 2.1.4 « Raccourcis avec une seule touche », niveau A ; « Raccourcis clavier
 * personnalises » de Gmail ; 1Password) : les raccourcis d'une seule touche (lettres, `/`, `?`) se COUPENT -- un
 * logiciel de dictee vocale ou une frappe accidentelle ne copie plus rien -- et chaque lettre se REASSIGNE. Une
 * preference de cet appareil, jamais un secret : relue comme une donnee NON FIABLE, toute valeur trafiquee retombe
 * au defaut. Ctrl+C / Ctrl+B et les touches de la recherche ne sont pas concernes (modificateur, ou actifs
 * seulement quand la recherche a le focus).
 */

export type ShortcutCommand =
  | 'focus-search'
  | 'clear-search'
  | 'next-entry'
  | 'previous-entry'
  | 'new-entry'
  | 'edit-entry'
  | 'copy-username'
  | 'copy-password'
  | 'copy-totp'
  | 'large-type'
  | 'lock'
  | 'show-shortcuts';

/** Ce que la commande lit d'un `KeyboardEvent` (un objet simple suffit : testable sans DOM). */
export interface ShortcutKey {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
  readonly isComposing?: boolean;
}

/**
 * Ou la touche a ete frappee : le champ de recherche du coffre, un autre champ de saisie, ou ailleurs (bouton,
 * page). `textSelected` : du texte est selectionne (dans la page ou dans le champ) -- Ctrl+C lui appartient alors.
 */
export interface ShortcutContext {
  readonly target: 'search' | 'editable' | 'other';
  readonly textSelected: boolean;
}

/** Les commandes d'une seule lettre : celles qui se reassignent. */
export type LetterCommand = 'next-entry' | 'previous-entry' | 'new-entry' | 'edit-entry' | 'copy-username' | 'copy-password' | 'copy-totp' | 'large-type' | 'lock';

/** Lettres par defaut, a la Gmail / GitHub. */
const LETTER_COMMANDS: Readonly<Record<string, LetterCommand>> = {
  j: 'next-entry',
  k: 'previous-entry',
  n: 'new-entry',
  e: 'edit-entry',
  u: 'copy-username',
  p: 'copy-password',
  t: 'copy-totp',
  g: 'large-type',
  l: 'lock',
};

/** Ordre d'affichage des commandes reassignables (Parametres, aide). */
export const LETTER_COMMAND_ORDER: readonly LetterCommand[] = ['next-entry', 'previous-entry', 'new-entry', 'edit-entry', 'copy-username', 'copy-password', 'copy-totp', 'large-type', 'lock'];

/** Ce que fait chaque commande reassignable, tel que l'affichent les Parametres. */
export const LETTER_COMMAND_LABELS: Readonly<Record<LetterCommand, string>> = {
  'next-entry': 'Entree suivante',
  'previous-entry': 'Entree precedente',
  'new-entry': 'Nouvelle entree',
  'edit-entry': 'Modifier l\'entree ouverte',
  'copy-username': 'Copier l\'identifiant',
  'copy-password': 'Copier le mot de passe',
  'copy-totp': 'Copier le code 2FA',
  'large-type': 'Mot de passe en grand format',
  lock: 'Verrouiller',
};

export interface ShortcutSettings {
  /** `false` : aucun raccourci d'une seule touche (lettres, `/`, `?`) -- WCAG 2.1.4. */
  readonly singleKey: boolean;
  /** La lettre de chaque commande, deux a deux distinctes. */
  readonly keys: Readonly<Record<LetterCommand, string>>;
}

/** Cle `localStorage` : une preference de cet appareil, jamais un secret. */
export const SHORTCUT_SETTINGS_KEY = 'klavox:shortcuts';

export const DEFAULT_SHORTCUT_SETTINGS: ShortcutSettings = {
  singleKey: true,
  keys: Object.fromEntries(Object.entries(LETTER_COMMANDS).map(([letter, command]) => [command, letter])) as Record<LetterCommand, string>,
};

/**
 * Une lettre de a a z, rien d'autre : un chiffre exige Maj sur un clavier AZERTY (et Maj n'est jamais pris), une
 * lettre accentuee n'existe pas sur tous les claviers, un signe peut etre une touche morte.
 */
export function isShortcutLetter(value: string): boolean {
  return /^[a-z]$/.test(value);
}

/**
 * Les reglages relus de `localStorage`, donnee non fiable : une lettre absente ou invalide reprend son defaut ;
 * deux commandes sur la meme lettre (valeur trafiquee) et c'est toute la table qui reprend ses defauts -- une
 * touche ne doit jamais faire deux choses. Seul un `false` explicite coupe les raccourcis d'une seule touche.
 */
export function readShortcutSettings(raw: string | null): ShortcutSettings {
  if (raw === null) return DEFAULT_SHORTCUT_SETTINGS;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_SHORTCUT_SETTINGS;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return DEFAULT_SHORTCUT_SETTINGS;
  const record = parsed as Record<string, unknown>;
  const singleKey = record.singleKey !== false;
  const stored = typeof record.keys === 'object' && record.keys !== null ? (record.keys as Record<string, unknown>) : {};
  const keys = {} as Record<LetterCommand, string>;
  for (const command of LETTER_COMMAND_ORDER) {
    const value = Object.hasOwn(stored, command) ? stored[command] : undefined;
    keys[command] = typeof value === 'string' && isShortcutLetter(value) ? value : DEFAULT_SHORTCUT_SETTINGS.keys[command];
  }
  if (new Set(Object.values(keys)).size !== LETTER_COMMAND_ORDER.length) return { singleKey, keys: DEFAULT_SHORTCUT_SETTINGS.keys };
  return { singleKey, keys };
}

/** Ce qui s'ecrit dans `localStorage` ; `null` = les reglages par defaut (la cle est alors retiree). */
export function serializeShortcutSettings(settings: ShortcutSettings): string | null {
  const isDefault = settings.singleKey && LETTER_COMMAND_ORDER.every((command) => settings.keys[command] === DEFAULT_SHORTCUT_SETTINGS.keys[command]);
  return isDefault ? null : JSON.stringify({ singleKey: settings.singleKey, keys: settings.keys });
}

export type ShortcutRebind = { readonly ok: true; readonly settings: ShortcutSettings } | { readonly ok: false; readonly error: string };

/**
 * Assigner une lettre a une commande. Refuse ce qui n'est pas une lettre de a a z et une lettre deja prise (comme
 * Gmail) : la commande qui la tient est nommee, rien n'est echange en silence.
 */
export function rebindShortcut(settings: ShortcutSettings, command: LetterCommand, raw: string): ShortcutRebind {
  if (!LETTER_COMMAND_ORDER.includes(command)) return { ok: false, error: 'Raccourci inconnu.' };
  const letter = raw.trim().toLowerCase();
  if (!isShortcutLetter(letter)) return { ok: false, error: `${LETTER_COMMAND_LABELS[command]} : une lettre de a a z, sans accent.` };
  const holder = LETTER_COMMAND_ORDER.find((other) => other !== command && settings.keys[other] === letter);
  if (holder !== undefined) return { ok: false, error: `« ${letter} » sert deja a : ${LETTER_COMMAND_LABELS[holder]}. Changez d'abord celle-ci.` };
  return { ok: true, settings: { singleKey: settings.singleKey, keys: { ...settings.keys, [command]: letter } } };
}

/**
 * La touche annoncee pour une commande (`aria-keyshortcuts`, titre, aide visuelle) ; `null` quand les raccourcis
 * d'une seule touche sont coupes : un bouton n'annonce jamais une touche qui ne fait rien.
 */
export function shortcutKeyOf(settings: ShortcutSettings, command: LetterCommand | 'focus-search' | 'show-shortcuts'): string | null {
  if (!settings.singleKey) return null;
  if (command === 'focus-search') return '/';
  if (command === 'show-shortcuts') return '?';
  return settings.keys[command];
}

/** La table lettre -> commande des reglages. */
function letterTable(settings: ShortcutSettings): Readonly<Record<string, ShortcutCommand>> {
  const table: Record<string, ShortcutCommand> = {};
  for (const command of LETTER_COMMAND_ORDER) table[settings.keys[command]] = command;
  return table;
}

/** La commande d'une touche dans son contexte ; `null` = la touche appartient a la page ou au champ. */
export function shortcutCommand(event: ShortcutKey, context: ShortcutContext, settings: ShortcutSettings = DEFAULT_SHORTCUT_SETTINGS): ShortcutCommand | null {
  if (event.isComposing === true || context.target === 'editable') return null;
  const modifier = event.ctrlKey || event.metaKey;
  if (modifier) {
    // Ctrl ET Cmd, Alt ou Maj : combinaisons du systeme ou du navigateur (Ctrl+Maj+C = inspecteur), jamais prises.
    if ((event.ctrlKey && event.metaKey) || event.altKey || event.shiftKey) return null;
    const key = event.key.toLowerCase();
    if (key === 'c') return context.textSelected ? null : 'copy-password';
    if (key === 'b') return 'copy-username';
    return null;
  }
  if (event.altKey) return null;
  if (context.target === 'search') {
    if (event.key === 'ArrowDown') return 'next-entry';
    if (event.key === 'ArrowUp') return 'previous-entry';
    if (event.key === 'Escape') return 'clear-search';
    return null;
  }
  // Raccourcis d'une seule touche coupes : `/`, `?` et les lettres restent a la page.
  if (!settings.singleKey) return null;
  // `/` et `?` exigent Maj sur un clavier AZERTY : Maj n'est donc pas refuse pour eux.
  if (event.key === '/') return 'focus-search';
  if (event.key === '?') return 'show-shortcuts';
  if (event.shiftKey) return null;
  const table = letterTable(settings);
  return Object.hasOwn(table, event.key) ? table[event.key]! : null;
}

/** Entree voisine dans l'ordre de la liste affichee ; sans selection, la premiere (ou la derniere en remontant). */
export function neighbourEntryId(ids: readonly string[], selectedId: string | null, step: 1 | -1): string | null {
  if (ids.length === 0) return null;
  const index = selectedId === null ? -1 : ids.indexOf(selectedId);
  if (index === -1) return step === 1 ? ids[0]! : ids[ids.length - 1]!;
  const next = index + step;
  return next < 0 || next >= ids.length ? null : ids[next]!;
}

/**
 * Signature d'un element focalisable, stable d'un rendu a l'autre : la vue est re-rendue en entier (`innerHTML`),
 * l'element qui avait le focus est remplace par un jumeau. Sans elle, le champ Rechercher perdait le focus a la
 * premiere lettre tapee (et a chaque seconde ou un code 2FA se rafraichit) : chercher au clavier etait impossible.
 * `null` : rien d'identifiable (l'element n'est pas retrouve, le focus n'est pas deplace au hasard).
 */
export function focusSignature(element: Element): string | null {
  const read = (name: string): string => element.getAttribute(name) ?? '';
  const form = element.closest('form')?.getAttribute('data-form') ?? '';
  const parts = [element.tagName, read('data-input'), read('data-action'), read('data-id'), read('data-field'), read('data-index'), read('name'), form];
  // Ni action, ni champ nomme, ni champ du coffre : un element anonyme ne se retrouve pas sans ambiguite.
  if (parts[1] === '' && parts[2] === '' && parts[6] === '') return null;
  return JSON.stringify(parts);
}
